import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to decode JWT token
function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const PurchaseInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Share modal states
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareChannel, setShareChannel] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [shareRecipient, setShareRecipient] = useState('');
  const [shareSubject, setShareSubject] = useState('');
  const [shareMessageText, setShareMessageText] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareInvoice, setShareInvoice] = useState<any | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<any | null>(null);
  const [bankMapModalOpen, setBankMapModalOpen] = useState(false);
  const [selectedInvoiceForMap, setSelectedInvoiceForMap] = useState<any | null>(null);
  const [selectedMapStatus, setSelectedMapStatus] = useState<number>(1);
  const [statementRows, setStatementRows] = useState<any[]>([]);
  const [selectedStatementRowId, setSelectedStatementRowId] = useState<string>("");
  const [tdsAmount, setTdsAmount] = useState<string>("0");

  // User auth state
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | number | null>(null);

  // Filters state
  const [filters, setFilters] = useState({
    companyId: "",
    vendorId: "",
    paidStatus: "",
    startDate: "",
    endDate: "",
    invoiceNo: "",
  });

  const triggerShare = (inv: any, channel: 'email' | 'sms' | 'whatsapp') => {
    const vend = vendors.find(v => String(v.id) === String(inv.Vendor_id));
    const comp = companies.find(c => String(c.id) === String(inv.Company_id));
    
    const vendName = vend ? vend.company_name : 'Vendor';
    const compName = comp ? comp.company_name : 'Our Company';
    
    let recipient = '';
    if (channel === 'email') {
      recipient = vend && vend.email ? vend.email : '';
    } else {
      recipient = vend && vend.number ? String(vend.number) : (vend && vend.phone ? String(vend.phone) : '');
    }

    let subject = `Purchase Bill Confirmation: ${inv.InvoiceNo}`;
    let message = `Hello ${vendName},\n\nWe have recorded/processed the purchase bill ${inv.InvoiceNo} dated ${new Date(inv.Date).toLocaleDateString('en-GB')}:\nTotal Amount: ₹ ${inv.Total_Amount}\nTDS Deducted: ₹ ${inv.TDSAmount || 0}\nStatus: ${inv.paid ? 'Paid' : 'Pending Payment'}\n\nRegards,\n${compName}`;

    if (channel === 'sms') {
      message = `Processed purchase bill ${inv.InvoiceNo} of ₹ ${inv.Total_Amount} from ${vendName}. Status: ${inv.paid ? 'Paid' : 'Pending'}. - ${compName}`;
    } else if (channel === 'whatsapp') {
      message = `Hello *${vendName}*,\n\nWe have recorded/processed the purchase bill *${inv.InvoiceNo}*:\n*Date:* ${new Date(inv.Date).toLocaleDateString('en-GB')}\n*Total Amount:* ₹ ${inv.Total_Amount}\n*TDS Amount:* ₹ ${inv.TDSAmount || 0}\n*Payment Status:* ${inv.paid ? 'Paid' : 'Pending'}\n\nRegards,\n*${compName}*`;
    }

    setShareInvoice(inv);
    setShareChannel(channel);
    setShareRecipient(recipient);
    setShareSubject(subject);
    setShareMessageText(message);
    setShareModalOpen(true);
  };

  const handleExecuteShare = async () => {
    if (!shareRecipient.trim() || !shareMessageText.trim()) {
      alert('Recipient and message text are required.');
      return;
    }
    setShareSending(true);
    try {
      await axios.post(`${API_URL}/auth/share`, {
        channel: shareChannel,
        recipient: shareRecipient,
        subject: shareSubject,
        messageText: shareMessageText,
        company_id: shareInvoice ? shareInvoice.Company_id : null
      });
      alert('Message shared successfully!');
      setShareModalOpen(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to share message.');
    } finally {
      setShareSending(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, compRes, vendRes, bsRows] = await Promise.all([
        axios.get(`${API_URL}/auth/purchase-invoices`),
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/vendors`),
        axios.get(`${API_URL}/auth/bank-statement-rows`).catch(() => ({ data: [] })),
      ]);
      setInvoices(invRes.data);
      setCompanies(compRes.data);
      setVendors(vendRes.data);
      setStatementRows(Array.isArray(bsRows.data) ? bsRows.data : []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    let decoded: any = null;
    if (token) {
      decoded = decodeToken(token);
    }
    setUserRole(storedRole || null);

    let compId: string | number | null = null;
    if (decoded) {
      compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
      setUserCompanyId(compId ? String(compId) : null);
    } else {
      setUserCompanyId(null);
    }

    // Default company filter based on active header selection or user's mapped company
    const selectedCompanyHeader = localStorage.getItem("selected_company_id");
    const defaultCompany = (storedRole !== "super-admin" && compId) 
      ? String(compId) 
      : (selectedCompanyHeader && selectedCompanyHeader !== "all" ? selectedCompanyHeader : "");

    setFilters(prev => ({
      ...prev,
      companyId: defaultCompany,
    }));

    fetchData();
  }, []);

  const getCompanyName = (id: string | number) => {
    const c = companies.find((x) => String(x.id) === String(id));
    return c ? c.company_name : "Unknown";
  };

  const getVendorName = (id: string | number) => {
    const v = vendors.find((x) => String(x.id) === String(id));
    return v ? v.company_name : "Unknown";
  };

  const handleUpdatePurchasePaidStatus = async (id: number, paidStatus: number, amountPaid?: number, paymentMode?: string) => {
    try {
      await axios.put(`${API_URL}/auth/purchase-invoiceremark/${id}`, {
        Paid: paidStatus,
        amount_paid: amountPaid,
        payment_mode: paymentMode
      });
      await fetchData();
    } catch (err) {
      alert("Failed to update payment status");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this purchase invoice?")) return;
    try {
      await axios.delete(`${API_URL}/auth/purchase-invoice/${id}`);
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
    } catch (err) {
      alert("Failed to delete invoice");
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      companyId: userRole === "super-admin" ? "" : (userCompanyId ? String(userCompanyId) : ""),
      vendorId: "",
      paidStatus: "",
      startDate: "",
      endDate: "",
      invoiceNo: "",
    });
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      // 1. Scoping Check: Company accounts must only see their own company purchase invoices
      if (userRole !== "super-admin" && userCompanyId) {
        if (String(inv.Company_id) !== String(userCompanyId)) {
          return false;
        }
      }

      // 2. Company Filter (available to Super Admin)
      if (filters.companyId && String(inv.Company_id) !== String(filters.companyId)) {
        return false;
      }

      // 3. Vendor Filter
      if (filters.vendorId && String(inv.Vendor_id) !== String(filters.vendorId)) {
        return false;
      }

      // 4. Paid Status Filter
      if (filters.paidStatus !== "") {
        const isPaid = Number(inv.paid);
        if (filters.paidStatus === "1" && isPaid !== 1) return false;
        if (filters.paidStatus === "0" && isPaid !== 0) return false;
        if (filters.paidStatus === "2" && isPaid !== 2) return false;
      }

      // 5. Date filters
      if (filters.startDate) {
        const invDate = new Date(inv.Date).getTime();
        const start = new Date(filters.startDate).getTime();
        if (invDate < start) return false;
      }
      if (filters.endDate) {
        const invDate = new Date(inv.Date).getTime();
        const end = new Date(filters.endDate).setHours(23, 59, 59, 999);
        if (invDate > end) return false;
      }

      // 6. Invoice No search
      if (filters.invoiceNo && !inv.InvoiceNo.toLowerCase().includes(filters.invoiceNo.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [invoices, filters, userRole, userCompanyId]);

  return (
    <div className="max-w-7xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-extrabold text-blue-800">Purchase Invoices</h2>
        <a href="/purchase-invoice-creator" className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow">
          + Record New Bill
        </a>
      </div>

      {/* Advanced Filters Section */}
      <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-2xl p-5 border border-blue-100 shadow-md mb-8">
        <div className="flex justify-between items-center border-b border-blue-100 pb-3 mb-4">
          <span className="text-blue-900 font-extrabold tracking-wide uppercase text-sm">🔍 Filters & Search</span>
          <button
            onClick={clearFilters}
            className="text-xs text-blue-600 hover:text-blue-800 font-bold bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 transition shadow-xs"
          >
            Clear Filters
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Company Filter - visible/enabled for super-admin */}
          {userRole === "super-admin" ? (
            <div>
              <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">Company</label>
              <select
                value={filters.companyId}
                onChange={e => handleFilterChange("companyId", e.target.value)}
                className="w-full text-xs font-semibold text-gray-700 border border-gray-300 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- All Companies --</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          ) : (
            // Hidden/Read-only scoping display if restricted
            userCompanyId && (
              <div>
                <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">Company</label>
                <div className="w-full text-xs font-bold text-blue-800 border border-blue-200 rounded-xl px-3 py-2 bg-blue-50/50">
                  {getCompanyName(userCompanyId)}
                </div>
              </div>
            )
          )}

          {/* Vendor Filter */}
          <div>
            <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">Vendor</label>
            <select
              value={filters.vendorId}
              onChange={e => handleFilterChange("vendorId", e.target.value)}
              className="w-full text-xs font-semibold text-gray-700 border border-gray-300 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- All Vendors --</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.company_name}</option>
              ))}
            </select>
          </div>

          {/* Payment Status Filter */}
          <div>
            <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">Payment Status</label>
            <select
              value={filters.paidStatus}
              onChange={e => handleFilterChange("paidStatus", e.target.value)}
              className="w-full text-xs font-semibold text-gray-700 border border-gray-300 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- All Statuses --</option>
              <option value="0">Unpaid</option>
              <option value="2">Partially Paid</option>
              <option value="1">Paid</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">From Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => handleFilterChange("startDate", e.target.value)}
              className="w-full text-xs font-semibold text-gray-700 border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">To Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => handleFilterChange("endDate", e.target.value)}
              className="w-full text-xs font-semibold text-gray-700 border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Invoice No Search */}
          <div>
            <label className="block text-[10px] font-extrabold text-blue-900/70 uppercase mb-1 tracking-wider">Bill No</label>
            <input
              type="text"
              value={filters.invoiceNo}
              onChange={e => handleFilterChange("invoiceNo", e.target.value)}
              className="w-full text-xs font-semibold text-gray-700 border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search bill number..."
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : filteredInvoices.length === 0 ? (
        <div className="text-center py-10 bg-white shadow rounded-2xl border text-gray-500">No purchase invoices match selected filters.</div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-xl rounded-2xl border">
          <table className="min-w-full text-left border-collapse">
            <thead>
              <tr className="bg-blue-50 border-b">
                <th className="p-4 font-bold text-blue-900">Date</th>
                <th className="p-4 font-bold text-blue-900">Invoice No</th>
                <th className="p-4 font-bold text-blue-900">Company</th>
                <th className="p-4 font-bold text-blue-900">Vendor</th>
                <th className="p-4 font-bold text-blue-900">Base+GST (₹)</th>
                <th className="p-4 font-bold text-blue-900">TDS (₹)</th>
                <th className="p-4 font-bold text-blue-900">Payment Status</th>
                <th className="p-4 font-bold text-blue-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((inv) => (
                <tr key={inv.id} className="border-b hover:bg-blue-50/50">
                  <td className="p-4">{new Date(inv.Date).toLocaleDateString("en-GB")}</td>
                  <td className="p-4 font-semibold text-gray-700">
                    <div className="flex items-center gap-2">
                      <span>{inv.InvoiceNo}</span>
                      {inv.attachment_url && (
                        <a
                          href={inv.attachment_url.startsWith('http') ? inv.attachment_url : `${API_URL}${inv.attachment_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-[10px] bg-blue-50 px-1.5 py-0.5 rounded font-bold transition hover:bg-blue-100 shadow-xs whitespace-nowrap"
                          title="View Uploaded Invoice File"
                        >
                          📎 View Bill
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="p-4">{getCompanyName(inv.Company_id)}</td>
                  <td className="p-4">{getVendorName(inv.Vendor_id)}</td>
                  <td className="p-4 font-semibold text-green-700">₹ {inv.Total_Amount}</td>
                  <td className="p-4 font-semibold text-red-600">₹ {inv.TDSAmount}</td>
                  <td className="p-4">
                    <div className="flex flex-col items-start gap-1">
                      <select
                        value={
                          Number(inv.paid) === 1
                            ? `1_${inv.payment_mode || 'bank'}`
                            : Number(inv.paid) === 2
                            ? `2_${inv.payment_mode || 'bank'}`
                            : "0"
                        }
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (val === "0") {
                            await handleUpdatePurchasePaidStatus(inv.id, 0, 0, 'bank');
                          } else {
                            const [statusStr, mode] = val.split('_');
                            const status = Number(statusStr);
                            if (mode === 'bank') {
                              setSelectedInvoiceForMap(inv);
                              setSelectedMapStatus(status);
                              setSelectedStatementRowId("");
                              setTdsAmount("0");
                              setBankMapModalOpen(true);
                            } else {
                              if (status === 2) {
                                const amtStr = window.prompt("Enter the amount paid (₹):", String(inv.amount_paid || ""));
                                if (amtStr !== null) {
                                  const amt = parseFloat(amtStr);
                                  if (!isNaN(amt) && amt >= 0) {
                                    await handleUpdatePurchasePaidStatus(inv.id, 2, amt, mode);
                                  } else {
                                    alert("Invalid amount entered.");
                                  }
                                }
                              } else {
                                await handleUpdatePurchasePaidStatus(inv.id, status, undefined, mode);
                              }
                            }
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-bold border cursor-pointer focus:outline-none transition-colors ${
                          Number(inv.paid) === 1
                            ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
                            : Number(inv.paid) === 2
                              ? "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200"
                              : "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200"
                        }`}
                      >
                        <option value="0">Unpaid</option>
                        <option value="2_bank">Partially Paid (Bank)</option>
                        <option value="2_cash">Partially Paid (Cash)</option>
                        <option value="1_bank">Paid (Bank)</option>
                        <option value="1_cash">Paid (Cash)</option>
                      </select>
                      {Number(inv.paid) === 2 && Number(inv.amount_paid) > 0 && (
                        <span className="text-[10px] font-semibold text-blue-700">
                          Paid: ₹{Number(inv.amount_paid).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                      {Number(inv.paid) > 0 && inv.payment_mode && (
                        <span className="text-[9px] uppercase font-bold text-gray-500 font-sans tracking-wider">
                          ({inv.payment_mode})
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 flex gap-1 items-center">
                    <button
                      onClick={() => setViewingInvoice(inv)}
                      className="text-indigo-600 hover:text-indigo-800 font-bold bg-indigo-50 px-2 py-1 rounded text-xs"
                    >
                      View
                    </button>
                    <button
                      onClick={() => navigate(`/purchase-invoice-creator?invoice_id=${inv.id}`)}
                      className="text-blue-500 hover:text-blue-700 font-bold bg-blue-50 px-2 py-1 rounded text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => triggerShare(inv, 'email')}
                      className="text-green-600 hover:text-green-800 font-bold bg-green-50 px-2 py-1 rounded text-xs"
                    >
                      Email
                    </button>
                    <button
                      onClick={() => triggerShare(inv, 'whatsapp')}
                      className="text-teal-600 hover:text-teal-800 font-bold bg-teal-50 px-2 py-1 rounded text-xs"
                    >
                      WhatsApp
                    </button>
                    <button onClick={() => handleDelete(inv.id)} className="text-red-500 hover:text-red-700 font-bold bg-red-50 px-2 py-1 rounded text-xs">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Share Notification Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl border border-blue-50 overflow-hidden text-left">
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Share Purchase Bill Details</h3>
              <button 
                onClick={() => setShareModalOpen(false)}
                className="text-white hover:text-gray-200 font-bold focus:outline-none text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Channel Selector */}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-2">Communication Channel</label>
                <div className="flex gap-2">
                  {[
                    { id: 'email', label: '✉️ Email', color: 'bg-green-50 text-green-700 border-green-200' },
                    { id: 'sms', label: '💬 SMS', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
                    { id: 'whatsapp', label: '🟢 WhatsApp', color: 'bg-teal-50 text-teal-700 border-teal-200' }
                  ].map(ch => (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => {
                        setShareChannel(ch.id as any);
                        if (shareInvoice) {
                          const vend = vendors.find(v => String(v.id) === String(shareInvoice.Vendor_id));
                          if (ch.id === 'email') {
                            setShareRecipient(vend && vend.email ? vend.email : '');
                          } else {
                            setShareRecipient(vend && vend.number ? String(vend.number) : (vend && vend.phone ? String(vend.phone) : ''));
                          }
                          const vendName = vend ? vend.company_name : 'Vendor';
                          const comp = companies.find(c => String(c.id) === String(shareInvoice.Company_id));
                          const compName = comp ? comp.company_name : 'Our Company';
                          if (ch.id === 'sms') {
                            setShareMessageText(`Processed purchase bill ${shareInvoice.InvoiceNo} of ₹ ${shareInvoice.Total_Amount} from ${vendName}. Status: ${shareInvoice.paid ? 'Paid' : 'Pending'}. - ${compName}`);
                          } else if (ch.id === 'whatsapp') {
                            setShareMessageText(`Hello *${vendName}*,\n\nWe have recorded/processed the purchase bill *${shareInvoice.InvoiceNo}*:\n*Date:* ${new Date(shareInvoice.Date).toLocaleDateString('en-GB')}\n*Total Amount:* ₹ ${shareInvoice.Total_Amount}\n*TDS Amount:* ₹ ${shareInvoice.TDSAmount || 0}\n*Payment Status:* ${shareInvoice.paid ? 'Paid' : 'Pending'}\n\nRegards,\n*${compName}*`);
                          } else {
                            setShareMessageText(`Hello ${vendName},\n\nWe have recorded/processed the purchase bill ${shareInvoice.InvoiceNo} dated ${new Date(shareInvoice.Date).toLocaleDateString('en-GB')}:\nTotal Amount: ₹ ${shareInvoice.Total_Amount}\nTDS Deducted: ₹ ${shareInvoice.TDSAmount || 0}\nStatus: ${shareInvoice.paid ? 'Paid' : 'Pending Payment'}\n\nRegards,\n${compName}`);
                          }
                        }
                      }}
                      className={`flex-1 py-2 px-3 text-xs font-bold border rounded-xl transition ${
                        shareChannel === ch.id 
                          ? 'border-blue-600 text-blue-700 bg-blue-50/50 ring-2 ring-blue-100'
                          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {ch.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">
                  {shareChannel === 'email' ? 'Recipient Email Address' : 'Recipient Phone Number'}
                </label>
                <input
                  type={shareChannel === 'email' ? 'email' : 'text'}
                  value={shareRecipient}
                  onChange={e => setShareRecipient(e.target.value)}
                  className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none"
                  placeholder={shareChannel === 'email' ? 'e.g. vendor@email.com' : 'e.g. 919876543210'}
                />
              </div>

              {/* Subject Input (Email Only) */}
              {shareChannel === 'email' && (
                <div>
                  <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">Email Subject</label>
                  <input
                    type="text"
                    value={shareSubject}
                    onChange={e => setShareSubject(e.target.value)}
                    className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none"
                    placeholder="Subject"
                  />
                </div>
              )}

              {/* Message Input */}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">Message Body</label>
                <textarea
                  value={shareMessageText}
                  onChange={e => setShareMessageText(e.target.value)}
                  rows={5}
                  className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none font-mono"
                  placeholder="Enter message text"
                />
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShareModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 bg-white border rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteShare}
                disabled={shareSending}
                className="px-5 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition disabled:opacity-50"
              >
                {shareSending ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingInvoice && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate__animated animate__fadeIn"
          onClick={() => setViewingInvoice(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl border border-blue-50 overflow-hidden text-left"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-800 to-indigo-700 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Purchase Bill Detail: {viewingInvoice.InvoiceNo}</h3>
              <button 
                onClick={() => setViewingInvoice(null)}
                className="text-white hover:text-gray-200 font-bold focus:outline-none text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-4">
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Vendor / Seller</span>
                  <span className="text-sm font-extrabold text-blue-900">{getVendorName(viewingInvoice.Vendor_id)}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Billing To</span>
                  <span className="text-sm font-extrabold text-blue-900">{getCompanyName(viewingInvoice.Company_id)}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4 text-xs font-semibold text-gray-700">
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Bill Date</span>
                  <span>{new Date(viewingInvoice.Date).toLocaleDateString("en-GB")}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Payment Status</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    Number(viewingInvoice.paid) === 1 
                      ? 'bg-green-100 text-green-800' 
                      : Number(viewingInvoice.paid) === 2 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {Number(viewingInvoice.paid) === 1 ? "Paid" : Number(viewingInvoice.paid) === 2 ? `Partially Paid (₹${viewingInvoice.amount_paid})` : "Unpaid"}
                  </span>
                </div>
                {viewingInvoice.remarks && (
                  <div className="col-span-3 mt-2">
                    <span className="block text-[10px] font-bold text-gray-400 uppercase">Remarks</span>
                    <p className="text-xs text-gray-600 bg-yellow-50/50 p-2 rounded-lg border border-yellow-100 italic">{viewingInvoice.remarks}</p>
                  </div>
                )}
                {viewingInvoice.attachment_url && (
                  <div className="col-span-3 mt-2 pt-2 border-t border-gray-100">
                    <span className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Attachment File</span>
                    <a
                      href={viewingInvoice.attachment_url.startsWith('http') ? viewingInvoice.attachment_url : `${API_URL}${viewingInvoice.attachment_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-bold bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-xl px-3 py-1.5 transition"
                    >
                      📎 View Uploaded Bill Document
                    </a>
                  </div>
                )}
              </div>

              {/* Items List */}
              <div>
                <span className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Particulars & Items</span>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-100 text-gray-500 font-bold">
                        <th className="p-3 text-left">Description</th>
                        <th className="p-3 text-left">HSN/SAC</th>
                        <th className="p-3 text-right">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let parsedParts: string[] = [];
                        let parsedHsns: string[] = [];
                        let parsedAmounts: string[] = [];
                        try { parsedParts = JSON.parse(viewingInvoice.Particulars || "[]"); } catch (e) {}
                        try { parsedHsns = JSON.parse(viewingInvoice.HSN_SAC || "[]"); } catch (e) {}
                        try { parsedAmounts = JSON.parse(viewingInvoice.Amounts || "[]"); } catch (e) {}
                        
                        const maxLen = Math.max(parsedParts.length, parsedHsns.length, parsedAmounts.length, 1);
                        const rows = [];
                        for (let i = 0; i < maxLen; i++) {
                          rows.push(
                            <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-slate-50/30">
                              <td className="p-3 font-medium text-gray-800">{parsedParts[i] || "—"}</td>
                              <td className="p-3 text-gray-500 font-mono">{parsedHsns[i] || "—"}</td>
                              <td className="p-3 text-right font-semibold text-gray-700">₹ {(Number(parsedAmounts[i]) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          );
                        }
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Calculations Block */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-2 border border-slate-100">
                <div className="flex justify-between text-xs font-medium text-gray-500">
                  <span>SGST Amount</span>
                  <span>₹ {(Number(viewingInvoice.SGSTAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs font-medium text-gray-500">
                  <span>CGST Amount</span>
                  <span>₹ {(Number(viewingInvoice.CGSTAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs font-medium text-gray-500 border-b pb-2">
                  <span>IGST Amount</span>
                  <span>₹ {(Number(viewingInvoice.IGSTAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs font-bold text-red-600 border-b pb-2">
                  <span>TDS Deducted</span>
                  <span>- ₹ {(Number(viewingInvoice.TDSAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm font-extrabold text-green-800 pt-1">
                  <span>Final Payable Amount</span>
                  <span>₹ {(Number(viewingInvoice.Total_Amount || 0) - Number(viewingInvoice.TDSAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-100 rounded-b-3xl">
              <button
                onClick={() => setViewingInvoice(null)}
                className="px-5 py-2 text-xs font-bold text-white bg-blue-700 hover:bg-blue-800 rounded-xl shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Statement Mapping Modal */}
      {bankMapModalOpen && selectedInvoiceForMap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBankMapModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-[500px] rounded-2xl shadow-2xl border border-blue-200 overflow-hidden m-4 text-left">
            <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white p-4 font-bold">
              Link Bank Statement Row: {selectedInvoiceForMap.InvoiceNo}
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-blue-900/80 mb-2 uppercase tracking-wide">
                  Select Bank Transaction
                </label>
                <select
                  value={selectedStatementRowId}
                  onChange={e => setSelectedStatementRowId(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 font-semibold"
                >
                  <option value="">-- Select Statement Row (Optional) --</option>
                  {statementRows
                    .filter(r => r.debit > 0 && !r.mapped_invoice_id && !r.mapped_purchase_id)
                    .map(r => (
                      <option key={r.id} value={r.id}>
                        {r.date_str ? r.date_str.substring(0, 10) : ''} | {r.particulars} | Debit: ₹{r.debit}
                      </option>
                    ))}
                </select>
                {statementRows.filter(r => r.debit > 0 && !r.mapped_invoice_id && !r.mapped_purchase_id).length === 0 && (
                  <span className="text-xs text-amber-600 font-semibold mt-1 block">
                    No unmapped bank statement debit rows found.
                  </span>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-blue-900/80 mb-2 uppercase tracking-wide">
                  TDS Deduction Amount (Optional, ₹)
                </label>
                <input
                  type="number"
                  value={tdsAmount}
                  onChange={e => setTdsAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setBankMapModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 bg-white border rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const amt = selectedMapStatus === 2 ? parseFloat(window.prompt("Enter the amount paid (₹):", String(selectedInvoiceForMap.amount_paid || "")) || "0") : undefined;
                  if (selectedMapStatus === 2 && (amt === undefined || isNaN(amt) || amt < 0)) {
                    alert("Invalid amount.");
                    return;
                  }
                  setBankMapModalOpen(false);
                  await handleUpdatePurchasePaidStatus(selectedInvoiceForMap.id!, selectedMapStatus, amt, 'bank');
                }}
                className="px-4 py-2 text-xs font-semibold text-amber-700 hover:text-amber-800 bg-white border border-amber-200 rounded-xl hover:bg-amber-50 transition"
              >
                Skip Link
              </button>
              <button
                type="button"
                disabled={!selectedStatementRowId}
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/auth/bank-statement-row/${selectedStatementRowId}/map`, {
                      mapped_purchase_id: selectedInvoiceForMap.id,
                      tds_amount: parseFloat(tdsAmount || "0"),
                      is_partial: selectedMapStatus === 2
                    });
                    setBankMapModalOpen(false);
                    await fetchData();
                  } catch (err: any) {
                    alert(err.response?.data?.error || "Failed to link statement row.");
                  }
                }}
                className="px-5 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition disabled:opacity-50"
              >
                Link & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseInvoices;
